import { NewsfeedFieldType } from '@benzinga/advanced-news-manager';
import { ExpressionObject } from '@benzinga/session';
import { isNumeric } from '@benzinga/utils';
import { SimpleNewsQuery, TidParam } from './entities';

export const generateUserIdQueryExpressionObject = (userId: TidParam): ExpressionObject<NewsfeedFieldType> => {
  const userIdList = Array.isArray(userId) ? userId : String(userId).split(',');
  const where: ExpressionObject<NewsfeedFieldType> = {
    group: {
      expression: ['UserID', 'any', userIdList],
      operator: 'expression',
    },
    operator: 'group',
  };
  return where;
};

export const generateTickersQueryExpressionObject = (
  tickers: string | string[],
): ExpressionObject<NewsfeedFieldType> => {
  const tickersList = Array.isArray(tickers) ? tickers : tickers.split(',');
  const where: ExpressionObject<NewsfeedFieldType> = {
    expression: ['Tickers.name', 'any', tickersList],
    operator: 'expression',
  };
  return where;
};

export const generateTagsQueryExpressionObject = (tags: TidParam): ExpressionObject<NewsfeedFieldType> => {
  const tagList = Array.isArray(tags) ? tags : String(tags).split(',');
  const key = isNumeric(tags[0] as string) ? 'Tags.tid' : 'Tags.name';
  const where: ExpressionObject<NewsfeedFieldType> = {
    expression: [key, 'any', tagList],
    operator: 'expression',
  };
  return where;
};

export const generateChannelsQueryExpressionObject = (channels: TidParam): ExpressionObject<NewsfeedFieldType> => {
  const channelList = Array.isArray(channels) ? channels : String(channels).split(',');
  const key = isNumeric(channels[0] as string) ? 'Channels.tid' : 'Channels.name';
  const where: ExpressionObject<NewsfeedFieldType> = {
    expression: [key, 'any', channelList],
    operator: 'expression',
  };
  return where;
};

export const generateChannelsAndTagsGroupExpressionObject = (
  channels: TidParam,
  tags: TidParam,
  operator: 'and' | 'or' = 'and',
): ExpressionObject<NewsfeedFieldType> => {
  const where: ExpressionObject<NewsfeedFieldType> = {
    group: {
      lhs: generateTagsQueryExpressionObject(tags),
      operator,
      rhs: generateChannelsQueryExpressionObject(channels),
    },
    operator: 'group',
  };
  return where;
};

export const generateSearchQueryExpressionObject = (query: SimpleNewsQuery): ExpressionObject<NewsfeedFieldType> => {
  let where = {} as ExpressionObject<NewsfeedFieldType>;

  if (query?.search) {
    const searchQuerySplit = query.search.split(' ');
    searchQuerySplit.forEach(searchTerm => {
      where = {
        lhs: {
          group: {
            lhs: {
              expression: ['Title', 'phrase', searchTerm],
              operator: 'expression',
            },
            operator: 'or',
            rhs: {
              expression: ['Body', 'phrase', searchTerm],
              operator: 'expression',
            },
          },
          operator: 'group',
        },
        operator: 'and',
        rhs: where,
      };
    });
  }

  if (query?.author) {
    where = {
      group: {
        lhs: {
          expression: ['name', 'phrase', query.author],
          operator: 'expression',
        },
        operator: 'and',
        rhs: where,
      },
      operator: 'group',
    };
  }

  if (query?.title) {
    where = {
      expression: ['Title', 'phrase', query.title],
      operator: 'expression',
    };
  }

  if (query?.body) {
    where = {
      group: {
        lhs: where,
        operator: 'and',
        rhs: {
          expression: ['Body', 'phrase', query.body],
          operator: 'expression',
        },
      },
      operator: 'group',
    };
  }
  return where;
};

export const convertSimpleQueryToInternalNewsQuery = (
  queries: SimpleNewsQuery[],
): ExpressionObject<NewsfeedFieldType> | null => {
  let expressionObject: null | any = null;

  if (Array.isArray(queries)) {
    queries.forEach(query => {
      if (Array.isArray(query.channels)) {
        if (expressionObject) {
          expressionObject = {
            lhs: expressionObject,
            operator: 'and',
            rhs: generateChannelsQueryExpressionObject(query.channels),
          };
        } else {
          expressionObject = generateChannelsQueryExpressionObject(query.channels);
        }
      }
      if (Array.isArray(query.tags)) {
        if (expressionObject) {
          query.tags.forEach(tag => {
            expressionObject = {
              lhs: expressionObject,
              operator: 'and',
              rhs: generateTagsQueryExpressionObject([tag as number]),
            };
          });
        } else {
          expressionObject = generateTagsQueryExpressionObject(query.tags);
        }
      }
      if (Array.isArray(query.tickers)) {
        if (expressionObject) {
          query.tickers.forEach(ticker => {
            expressionObject = {
              lhs: expressionObject,
              operator: 'and',
              rhs: generateTickersQueryExpressionObject(ticker),
            };
          });
        } else {
          expressionObject = generateTickersQueryExpressionObject(query.tickers);
        }
      }
      // if (query?.title) {
      //   if (expressionObject) {
      //     expressionObject = {
      //       lhs: expressionObject,
      //       operator: 'and',
      //       rhs: generateSearchQueryExpressionObject({ title: query.title }),
      //     };
      //   } else {
      //     expressionObject = generateTickersQueryExpressionObject(query.title);
      //   }
      // }
    });
  }
  return expressionObject;
};
